import type { RowData, Table } from "@tanstack/react-table";
import { Button } from "./Button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./DropDown";

import { ArrowUpDownIcon, FilterIcon, SettingsIcon } from "lucide-react";
import { cn } from "@repo/shared/utils";

export type LayoutSaveStrategy = "automatic" | "user";

export interface DataTableColumnsDropdownProps<T extends RowData> {
  className?: string;
  table: Table<T>;
  onSave?: () => void;
  onRestore?: () => void;
  onReset?: () => void;
  layoutSaveStrategy?: LayoutSaveStrategy;
  full?: boolean;
}

export const DataTableColumnsDropdown = <T extends RowData>({
  className,
  table,
  onSave,
  onRestore,
  onReset,
  layoutSaveStrategy,
  full = true,
}: DataTableColumnsDropdownProps<T>) => {
  const hasHiddenFilters = table
    .getAllColumns()
    .some((column) => column.getIsFiltered() && !column.getIsVisible());

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {full ? (
          <Button variant="ghost" className={cn("ml-3 pr-2 pl-1", className)}>
            <SettingsIcon className="h-4" /> Columns
          </Button>
        ) : (
          <Button
            tooltip={
              hasHiddenFilters ? (
                <>
                  One or more hidden columns have a<br />
                  filter applied, which may cause some
                  <br />
                  rows to be hidden unexpectedly.
                </>
              ) : (
                "Manage Columns"
              )
            }
            tooltipProps={{
              className: cn(hasHiddenFilters && "bg-yellow-200 text-black"),
            }}
            variant={hasHiddenFilters ? "destructive" : "ghost"}
            className={cn("p-1 w-8 h-8", className)}
          >
            <SettingsIcon className="h-5 opacity-80" />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={full ? "end" : "start"}
        className="max-h-[60vh] overflow-y-auto"
      >
        {table
          .getAllColumns()
          .filter((column) => column.getCanHide())
          .map((column) => {
            const visible = column.getIsVisible();
            const filtered = column.getIsFiltered();
            const sorted = column.getIsSorted();

            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className={cn(
                  "capitalize",
                  filtered && !visible && "bg-yellow-200  hover:bg-destructive"
                )}
                checked={visible}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
                onSelect={(evt) => evt.preventDefault()}
              >
                <span className="mr-auto">
                  {column.columnDef?.meta?.label ?? column.id}
                </span>
                {!!sorted && <ArrowUpDownIcon className="w-5 h-3 ml-0.5" />}
                {filtered && <FilterIcon className="w-5 h-3 ml-0.5" />}
              </DropdownMenuCheckboxItem>
            );
          })}
        <DropdownMenuSeparator />
        {layoutSaveStrategy === "user" && onSave && (
          <>
            <DropdownMenuItem className="font-bold" onClick={onSave}>
              Save Layout
            </DropdownMenuItem>
            <DropdownMenuItem className="font-bold" onClick={onRestore}>
              Restore Last Saved
            </DropdownMenuItem>
          </>
        )}
        {onReset && (
          <DropdownMenuItem className="font-bold" onClick={onReset}>
            Restore Factory Default
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
